import { State } from 'interfaces/state'
import { Fetchable } from 'interfaces/fetchable'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { useFormContext, FieldError } from 'react-hook-form'
import { useEffect } from 'react'
import { futureRequestSelector, layerPostRequestSelector } from 'components/features/future/selectors'
import { FutureLayerFields } from 'components/features/future/types'
import { TYPE_REQUIRED } from 'constants/errors'
import { fetchFieldErrors } from 'helpers/errorHelpers'

const useRestErrorsSource = (selector: (state: State) => Fetchable<any>) => {
  const restErrors = useStoreSelector(selector).errorsMap
  const { setError } = useFormContext<Record<string, any>>()
  useEffect(() => {
    if (!restErrors) {
      return
    }

    const keys = Object.keys(restErrors)
    if (!keys || !keys.length) {
      return
    }

    keys.forEach((key) => {
      const error = restErrors[key][0]
      setError(key, {
        type: error,
        message: error,
      })
    })
  }, [restErrors, setError])
}

export const useCollectErrors = <FormType>(): Partial<Record<keyof FormType, string>> => {
  useRestErrorsSource(futureRequestSelector)

  const { formState: { errors }} = useFormContext<FormType>()
  return fetchFieldErrors(errors)
}

const useWhenErrorsSource = () => {
  const { setError, clearErrors, formState: { errors }} = useFormContext<Record<string, any>>()

  const whenErrors = (errors.when || {}) as Record<string, FieldError>
  const fieldsErrors = Object.keys(whenErrors)
  const hasError = fieldsErrors.length

  if (!hasError) {
    if (errors.whenField) {
      clearErrors('whenField')
    }

    return
  }

  const fieldError = whenErrors[fieldsErrors[0]]
  if (errors.whenField && fieldError && errors.whenField.message === fieldError.message) {
    return
  }

  const message = fieldError && fieldError.message || TYPE_REQUIRED
  setError('whenField', {
    type: message,
    message,
  })
}

export const useCollectLayerErrors = <T extends FutureLayerFields>(): Partial<Record<(keyof T) | 'whenField', string>> => {
  useRestErrorsSource(layerPostRequestSelector)
  useWhenErrorsSource()

  const { formState: { errors }} = useFormContext<T>()
  return fetchFieldErrors(errors)
}
